import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { matchesApi } from "../lib/api";
import { setCachedMatches } from "../lib/matchListCache";
import MegaLobby, { MegaCard, useMegaLaunch } from "../components/MegaLobby";
import GlossCard from "../components/GlossCard";
import { getGames, peekGames, useAllGamesReady } from "../lib/megaCache";
import HeroCarousel from "../components/HeroCarousel";
import NewsTicker from "../components/NewsTicker";

const IMG = {
  sports: "/img/sports.webp",
  depositWithdraw: "/img/depositWithdraw.webp",
  referEarn: "/img/referEarn.webp",
  weeklyBonus: "/img/weeklyBonus.webp",
  aviator: "/img/aviator.webp",
  affiliate: "/img/affiliate.webp",
  roulette: "/img/roulette_model.webp",
  miniRoulette: "/img/miniRoulette_model.webp",
  proRoulette: "/img/proRoulette_model.webp",
  dice: "/img/dice_model.webp",
  mines: "/img/mines_model.webp",
  hilo: "/img/hilo_model.webp",
  plinko: "/img/plinko_model.webp",
  tower: "/img/tower_model.webp",
  limbo: "/img/limbo_model.webp",
  coinflip: "/img/coinflip.webp",
  liveCasino: "/img/live_casino_banner.webp",
};

const CARDS = [
  { to: "/live-casino", name: "Live Casino", img: IMG.liveCasino, wide: true },
  { to: "/games/roulette", name: "Roulette", img: IMG.roulette },
  { to: "/games/mini-roulette", name: "Mini Roulette", img: IMG.miniRoulette },
  { to: "/games/pro-roulette", name: "Pro Roulette", img: IMG.proRoulette },
  { to: "/games/dice", name: "Lucky Dice", img: IMG.dice },
  { to: "/games/mines", name: "Gem Miner", img: IMG.mines },
  { to: "/games/hilo", name: "Hi-Lo Rush", img: IMG.hilo },
  { to: "/games/plinko", name: "Plinko", img: IMG.plinko },
  { to: "/games/tower", name: "Tower", img: IMG.tower },
  { to: "/games/limbo", name: "Limbo", img: IMG.limbo },
  { to: "/games/coinflip", name: "Coin Flip", img: IMG.coinflip },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

function Card({ c, i }) {
  return (
    <GlossCard light to={c.soon ? undefined : c.to} name={c.name} wide={c.wide} soon={c.soon} testid={`casino-card-${slug(c.name)}`} nameTestid={`casino-card-name-${slug(c.name)}`} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
      <img src={c.img} alt={c.name} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
      {c.soon && <span className="absolute top-2 left-2 rounded-sm bg-[#e53935] px-1.5 py-0.5 text-[10px] font-bold text-white">Coming Soon</span>}
    </GlossCard>
  );
}

function LiveSportsCard({ liveCount }) {
  return (
    <GlossCard light to="/sports" name="Live Sports" wide testid="home-live-sports-card">
      <img src={IMG.sports} alt="Live Sports" className="absolute inset-0 h-full w-full object-cover" decoding="async" />
      <div className="absolute inset-0 bg-gradient-to-l from-black/80 via-black/20 to-transparent" />
      <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-sm bg-[#e53935] px-1.5 py-0.5 text-[11px] font-extrabold text-white">
          <Radio className="h-3 w-3" /> LIVE
        </span>
        <ul className="text-white text-[12px] font-semibold space-y-1 pr-0.5">
          {[["Cricket", liveCount], ["Soccer", 0], ["Tennis", 0]].map(([n, c]) => (
            <li key={n} className="flex items-center justify-end gap-2">
              <span>{n}</span>
              <span className="grid place-items-center min-w-[20px] h-[18px] rounded-sm bg-white text-[#0f2340] text-[11px] font-bold px-1" data-testid={`home-live-count-${n.toLowerCase()}`}>{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </GlossCard>
  );
}

const AVIATOR = { provider_code: 15, game_symbol: "aviator", name: "Aviator" };
const NOT_AVIATOR = (g) => g.game_symbol !== AVIATOR.game_symbol;
// Pragmatic live tables pinned on Home (also listed in the Live Casino tab):
// Candyland + Mega Wheel between Live Sports and Live Casino; Money Time + Mega Roulette between Live Casino and Roulette.
const FEATURED_TOP = [{ provider_code: 1, game_symbol: "1101" }, { provider_code: 1, game_symbol: "801" }];
const FEATURED_MID = [{ provider_code: 1, game_symbol: "2750" }, { provider_code: 1, game_symbol: "204" }];

function FeaturedLive({ items }) {
  const pick = (d) => items.map((f) => (d?.games || []).find((g) => g.provider_code === f.provider_code && g.game_symbol === f.game_symbol)).filter(Boolean);
  const [games, setGames] = useState(() => pick(peekGames("Live")));
  const { launch, busy } = useMegaLaunch();
  useEffect(() => {
    let dead = false;
    getGames("Live").then((d) => { if (!dead) setGames(pick(d)); }).catch(() => {});
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  return games.map((g) => <MegaCard key={g.game_symbol} g={g} onLaunch={launch} busy={busy} variant="home" eager />);
}

// Hub tables pinned on Home right under Live Sports (above Candyland + Mega Wheel): SA Gaming row first, then Ezugi row.
const HUB_FEATURED = ["C Roulette", "Baccarat C01", "Andar Bahar", "Teen Patti 3 Card"];
const HUB_AVIATRIX = ["Aviatrix", "Aviatrix Fruits"];  // crash games row between Live Casino and Money Time / Mega Roulette
function FeaturedHub({ names = HUB_FEATURED }) {
  const pick = (d) => names.map((n) => (d?.games || []).find((g) => g.name === n)).filter(Boolean);
  const [games, setGames] = useState(() => pick(peekGames("Hub")));
  const { launch, busy } = useMegaLaunch();
  useEffect(() => {
    let dead = false;
    getGames("Hub").then((d) => { if (!dead) setGames(pick(d)); }).catch(() => {});
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.join("|")]);
  return games.map((g) => <MegaCard key={g.game_symbol} g={g} onLaunch={launch} busy={busy} variant="home" eager />);
}

// 4 random Ezugi tables under the Home slot row (after Chicken Rush + Fortune Rabbit); one per game type, re-shuffled each visit.
const pickRandomHub = (d) => {
  const pool = (d?.games || []).filter((g) => !HUB_FEATURED.includes(g.name) && !HUB_AVIATRIX.includes(g.name) && g.category !== "lobby" && !g.maintenance).sort(() => Math.random() - 0.5);
  const seen = new Set();
  return pool.filter((g) => !seen.has(g.category) && seen.add(g.category)).slice(0, 4);
};
function RandomHub() {
  const [games, setGames] = useState(() => pickRandomHub(peekGames("Hub")));
  const { launch, busy } = useMegaLaunch();
  useEffect(() => {
    let dead = false;
    if (games.length) return undefined;
    getGames("Hub").then((d) => { if (!dead) setGames(pickRandomHub(d)); }).catch(() => {});
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!games.length) return null;
  return (
    <div className="grid grid-cols-2 gap-1.5 pt-1.5" data-testid="home-random-ezugi">
      {games.map((g) => <MegaCard key={g.game_symbol} g={g} onLaunch={launch} busy={busy} variant="home" />)}
    </div>
  );
}

export default function Casino() {
  const [liveCount, setLiveCount] = useState(0);
  const ready = useAllGamesReady();  // all three catalogues cached → every section paints in one pass
  const { launch: megaLaunch, busy: megaBusy } = useMegaLaunch();
  useEffect(() => {
    let dead = false;
    matchesApi.list()
      .then((d) => { setCachedMatches(d); if (!dead) setLiveCount((d || []).filter((m) => m.status === "live").length); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  return (
    <div className="max-w-3xl mx-auto sm:px-6 sm:py-5 bg-[#0b0b0b]" data-testid="casino-page">
      <NewsTicker />
      <div className="w-full">
        <HeroCarousel
          slides={[
            { key: "aviator", onClick: () => megaLaunch(AVIATOR), busy: megaBusy === AVIATOR.game_symbol, img: IMG.aviator, badge: "Spribe · Live", badgeClass: "bg-orange-500 text-black", title: "Aviator — Catch Your Luck", subtitle: "Fly high, cash out before it flies away", cta: "Play Now" },
            { key: "affiliate", to: "/affiliate", img: IMG.affiliate, badge: "Earn 10%", badgeClass: "bg-teal-500 text-black", title: "Affiliate Account", subtitle: "Bring 5 members & earn 10% as an affiliate maker", cta: "Start Earning" },
            { key: "refer", to: "/wallet", img: IMG.referEarn, badge: "Refer & Earn", badgeClass: "bg-fuchsia-500 text-white", title: "Invite Friends, Earn ₹500", subtitle: "Share batballworld.com · earn on every friend", cta: "Get My Link" },
            { key: "deposit", to: "/wallet?tab=deposit", img: IMG.depositWithdraw, badge: "Fast & Secure", badgeClass: "bg-[#E0B84C] text-black", title: "Instant Withdrawals & Deposits", subtitle: "Every 1 hour delay in withdrawal = ₹100/hour paid to you as fine", cta: "Add Money" },
            { key: "weekly", to: "/wallet", img: IMG.weeklyBonus, badge: "Weekly Reward", badgeClass: "bg-emerald-500 text-black", title: "Weekly Losing Bonus", subtitle: "Get cashback on your weekly losses", cta: "Learn More" },
          ]}
        />
      </div>

      <div className="bbw-home-panel" data-testid="home-light-panel">
      {!ready ? (
        <div className="grid grid-cols-2 gap-1.5" data-testid="home-skeleton">
          <div className="col-span-2"><LiveSportsCard liveCount={liveCount} /></div>
          {Array.from({ length: 14 }).map((_, i) => <div key={i} className="aspect-[1.6] rounded-xl bg-white/[0.06] animate-pulse" />)}
        </div>
      ) : (<>
      <div className="grid grid-cols-2 gap-1.5" data-testid="home-game-grid">
        <div className="col-span-2"><LiveSportsCard liveCount={liveCount} /></div>
        <FeaturedHub />
        <FeaturedLive items={FEATURED_TOP} />
        <Card c={CARDS[0]} i={0} />
        <FeaturedHub names={HUB_AVIATRIX} />
        <FeaturedLive items={FEATURED_MID} />
        {CARDS.slice(1).map((c, i) => <Card key={c.name} c={c} i={i + 1} />)}
      </div>
      <MegaLobby category="Slot" variant="home" emptyText="" match={NOT_AVIATOR} />
      <RandomHub />
      </>)}
      </div>
    </div>
  );
}
